import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Save, Eye, Send, History } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { ContentBlocksEditor, DocContentView, StatusBadge, STATUS_META } from "@/components/shared/DocKit";

export default function TemplateEditor() {
  const { templateId } = useParams();
  const isNew = templateId === "neu";
  const navigate = useNavigate();
  const { t } = useLang();
  const [tpl, setTpl] = useState(null);
  const [meta, setMeta] = useState({ categories: [], placeholders: [] });
  const [blocks, setBlocks] = useState([]);
  const [versions, setVersions] = useState([]);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/docs/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/docs/blocks").then((r) => setBlocks(r.data)).catch(() => {});
    if (isNew) {
      setTpl({ name: "", category: "compliance", description: "", content: [], status: "draft", version: 0 });
    } else {
      api.get(`/docs/templates/${templateId}`).then((r) => setTpl(r.data)).catch(() => navigate("/admin/dokumente"));
      api.get(`/docs/templates/${templateId}/versions`).then((r) => setVersions(r.data)).catch(() => {});
    }
  }, [templateId, isNew, navigate]);
  useEffect(load, [load]);

  if (!tpl) return <div className="space-y-4"><Skeleton className="h-8 w-64" /><Skeleton className="h-96 rounded-[16px]" /></div>;

  const payload = () => ({ name: tpl.name, category: tpl.category, description: tpl.description || "", content: tpl.content || [], status: tpl.status });

  const save = async () => {
    if (!tpl.name.trim()) return toast.error(t("Name erforderlich", "Name required"));
    try {
      setSaving(true);
      if (isNew) {
        const res = await api.post("/docs/templates", payload());
        toast.success(t("Vorlage erstellt", "Template created"));
        navigate(`/admin/dokumente/vorlagen/${res.data.id}`, { replace: true });
      } else {
        const res = await api.patch(`/docs/templates/${templateId}`, payload());
        setTpl(res.data);
        toast.success(t("Vorlage gespeichert", "Template saved"));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Fehler beim Speichern", "Error saving"));
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    try {
      await api.patch(`/docs/templates/${templateId}`, payload());
      const res = await api.post(`/docs/templates/${templateId}/publish`);
      setTpl(res.data);
      api.get(`/docs/templates/${templateId}/versions`).then((r) => setVersions(r.data));
      toast.success(t(`Ver\u00f6ffentlicht als Version ${res.data.version}`, `Published as version ${res.data.version}`));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Fehler beim Ver\u00f6ffentlichen", "Error publishing"));
    }
  };

  const doPreview = async () => {
    try {
      await api.patch(`/docs/templates/${templateId}`, payload());
      const res = await api.post(`/docs/templates/${templateId}/preview`, { data_source: { type: "manual" } });
      setPreview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Vorschau fehlgeschlagen", "Preview failed"));
    }
  };

  return (
    <div className="space-y-6" data-testid="template-editor">
      <div>
        <Link to="/admin/dokumente" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ChevronLeft className="h-4 w-4" /> {t("Dokumente & Vorlagen", "Documents & templates")}
        </Link>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-semibold tracking-tight">{isNew ? t("Neue Vorlage", "New template") : tpl.name}</h1>
            <div className="mt-1.5 flex items-center gap-2">
              <StatusBadge status={tpl.status} />
              <Badge variant="secondary" className="rounded-full text-[10px]">v{tpl.version || 0}</Badge>
              {versions.length > 0 && (
                <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><History className="h-3 w-3" /> {versions.length} {t("Versionen", "versions")}</span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {!isNew && (
              <Button variant="outline" className="rounded-[10px]" onClick={doPreview} data-testid="template-preview-button">
                <Eye className="h-4 w-4 mr-1.5" /> {t("Vorschau", "Preview")}
              </Button>
            )}
            <Button variant="outline" className="rounded-[10px]" onClick={save} disabled={saving} data-testid="template-save-button">
              <Save className="h-4 w-4 mr-1.5" /> {t("Speichern", "Save")}
            </Button>
            {!isNew && (
              <Button className="rounded-[10px] bg-[#0B1220]" onClick={publish} data-testid="template-publish-button">
                <Send className="h-4 w-4 mr-1.5" /> {t("Ver\u00f6ffentlichen", "Publish")} (v{(tpl.version || 0) + 1})
              </Button>
            )}
          </div>
        </div>
      </div>

      <Card className="rounded-[16px]">
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5 sm:col-span-1">
              <Label>{t("Name", "Name")}</Label>
              <Input value={tpl.name} onChange={(e) => setTpl({ ...tpl, name: e.target.value })} className="rounded-[10px]" data-testid="template-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("Kategorie", "Category")}</Label>
              <Select value={tpl.category} onValueChange={(v) => setTpl({ ...tpl, category: v })}>
                <SelectTrigger className="rounded-[10px]" data-testid="template-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {meta.categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={tpl.status} onValueChange={(v) => setTpl({ ...tpl, status: v })}>
                <SelectTrigger className="rounded-[10px]" data-testid="template-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(STATUS_META).map((s) => <SelectItem key={s} value={s}>{t(STATUS_META[s].label, STATUS_META[s].en)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t("Beschreibung", "Description")}</Label>
            <Textarea rows={2} value={tpl.description || ""} onChange={(e) => setTpl({ ...tpl, description: e.target.value })} className="rounded-[10px]" />
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-[16px]">
        <CardContent className="p-6">
          <h3 className="text-sm font-semibold mb-4">{t("Inhalt", "Content")}</h3>
          <ContentBlocksEditor
            content={tpl.content}
            onChange={(content) => setTpl({ ...tpl, content })}
            placeholders={meta.placeholders}
            blocks={blocks}
          />
        </CardContent>
      </Card>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{t("Vorschau", "Preview")}: {preview?.name}</DialogTitle>
          </DialogHeader>
          {preview && <DocContentView content={preview.content} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
